import { useCallback, useEffect, useMemo, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Button, Grid, Stack, Tab } from "@mui/material";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NavigateIcon } from "../../../assets/icons";
import { AppMenuKey, AppMenuSectionItem, AppRoutes } from "../../../common/routesUtils";
import DropdownSelect from "../../../components/common/DropdownSelect";
import { View } from "../../../components/layout/View";
import { PageType } from "../../../config/page.types";
import { useBreadcrumbs } from "../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../context/PageConfigrations/usePageConfigurationState";
import { usePeriodFilter } from "../../../hooks/usePreferences";
import Greenrouter from "./tabs/Greenrouter";
import {
	electricityDataCol,
	getElectricityColumns,
	getElectricityRows,
} from "./tabs/Greenrouter/data/sources/electricity";
import { fuelsDataCols, getFuelsColumns, getFuelsRows } from "./tabs/Greenrouter/data/sources/fuels";
import { getHeatSteamColumns, getHeatSteamRows, heatSteamDataCols } from "./tabs/Greenrouter/data/sources/heatSteam";
import {
	getProcessGasColumns,
	getProcessGasRows,
	processGasDataCols,
} from "./tabs/Greenrouter/data/sources/processGas";
import {
	getRefrigerantsColumns,
	getRefrigerantsRows,
	refrigerantsDataCols,
} from "./tabs/Greenrouter/data/sources/refrigerants";
import { getWaterColumns, getWaterRows, waterDataCols } from "./tabs/Greenrouter/data/sources/water";
import Overview from "./tabs/Overview";
import ThirdParty from "./tabs/ThirdParty";
import { MapBounds, useOperationalUnitsData } from "./useOperationalUnitsData";

interface Props {
	menuKeys?: Array<AppMenuKey>;
	path?: Array<AppMenuSectionItem>;
}

// Keys for the dropdown select
type EmissionsKey = "co2Wtw";

/**
 * OperationalUnits
 * Handles overview, greenrouter and third party operational units.
 * Each tab renders emissions, consumptions and map-based data queries.
 */
export default function OperationalUnits({ menuKeys, path }: Props) {
	const { t } = useTranslation(["feature.units", "common"]);
	const { formattedFrom: from, formattedTo: to } = usePeriodFilter();

	const { appliedFilters } = useFiltersRegistry();

	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	// Page configuration, stored in localStorage by PageType
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsOperationalUnits, {
		emissionsKey: "co2Wtw",
		tabValue: "overview",
		overviewEmissionsIntervalsPeriodType: "month",
		overviewIntervalMode: "emissions",
		greenrouterSelectedTabConsumption: "electricity",
	});

	// Select emission for all the page
	const dropdownSelectEmissions = [{ key: "co2Wtw", label: t("common:wtw select") }];

	const handleEmissionChange = (key: string) => {
		setConfig({ emissionsKey: key as EmissionsKey });
	};

	// MAP STATE
	const [mapBounds, setMapBounds] = useState<MapBounds | undefined>(undefined);

	/**
	 * Debounced state update for map bounds
	 * - Prevents excessive re-renders and query spam when user moves the map
	 * - 500ms delay after map stops moving
	 */
	const debouncedSetMapBounds = useMemo(
		() =>
			debounce((newBounds: MapBounds) => {
				setMapBounds((prev) => {
					const changed =
						prev?.min_lat !== newBounds.min_lat ||
						prev?.max_lat !== newBounds.max_lat ||
						prev?.min_lon !== newBounds.min_lon ||
						prev?.max_lon !== newBounds.max_lon ||
						prev?.zoom !== newBounds.zoom;
					return changed ? newBounds : prev;
				});
			}, 500),
		[]
	);

	// Cleanup to avoid memory leaks when component unmounts
	useEffect(() => {
		return () => {
			debouncedSetMapBounds.cancel();
		};
	}, [debouncedSetMapBounds]);

	/**
	 * Handler passed to the Map component
	 * Receives new map bounds and triggers the debounced update.
	 */
	const handleBoundsChange = useCallback(
		(bounds: MapBounds) => {
			debouncedSetMapBounds(bounds);
		},
		[debouncedSetMapBounds]
	);

	/**
	 * Payload for data fetching
	 * Automatically re-memoized when filters or period change
	 */
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const { emissions, consumption, maps, thirdParty, fileDownload } = useOperationalUnitsData({
		payload,
		periodType: config.overviewEmissionsIntervalsPeriodType,
		tab: config.tabValue,
		from,
		to,
		mapBounds,
	});

	const handleChange = (event: React.SyntheticEvent, newValue: "overview" | "greenrouter" | "thirdparty") => {
		setConfig({ tabValue: newValue });
	};

	/**
	 * Prepares consumption data tables
	 */
	const consumptionSources = useMemo(() => {
		const sources = consumption.sources;
		if (
			!sources.electricity.data ||
			!sources.fuel.data ||
			!sources.refrigerants.data ||
			!sources.processGas.data ||
			!sources.heatSteam.data ||
			!sources.water.data
		) {
			return;
		}

		return {
			electricity: {
				rows: getElectricityRows(sources.electricity.data, t),
				columns: getElectricityColumns(electricityDataCol, t),
			},
			fuel: {
				rows: getFuelsRows(sources.fuel.data, t),
				columns: getFuelsColumns(fuelsDataCols, t),
			},
			refrigerants: {
				rows: getRefrigerantsRows(sources.refrigerants.data, t),
				columns: getRefrigerantsColumns(refrigerantsDataCols, t),
			},
			processGas: {
				rows: getProcessGasRows(sources.processGas.data, t),
				columns: getProcessGasColumns(processGasDataCols, t),
			},
			heatSteam: {
				rows: getHeatSteamRows(sources.heatSteam.data, t),
				columns: getHeatSteamColumns(heatSteamDataCols, t),
			},
			water: {
				rows: getWaterRows(sources.water.data, t),
				columns: getWaterColumns(waterDataCols, t),
			},
		};
	}, [consumption.sources, t]);

	// RENDER NAVIGATION BUTTON
	const renderNavigationButton = () => {
		if (config.tabValue === "greenrouter") {
			return (
				<Button
					variant="contained"
					color="primary"
					onClick={() => {
						navigate(AppRoutes.ResultsOperationalUnitsGreenRouter);
						visitPage(PageType.ResultsOperationalUnitsGreenRouter);
					}}
					endIcon={<NavigateIcon color="inherit" />}
				>
					{t("view greenrouter operational units")}
				</Button>
			);
		}
		if (config.tabValue === "thirdparty") {
			return (
				<Button
					variant="contained"
					color="primary"
					onClick={() => {
						navigate(AppRoutes.ResultsOperationalUnitsThirdParty);
						visitPage(PageType.ResultsOperationalUnitsThirdParty);
					}}
					endIcon={<NavigateIcon color="inherit" />}
				>
					{t("view third party operational units")}
				</Button>
			);
		}
		return null;
	};

	return (
		<View
			pageType={PageType.ResultsOperationalUnits}
			headerContext={{
				payload,
				fileDownload,
				t,
			}}
		>
			<TabContext value={config.tabValue}>
				<Grid container spacing={2}>
					<Grid size={12}>
						<Stack direction={"row"} spacing={2} justifyContent={"space-between"}>
							<Stack direction={"row"} spacing={2}>
								<TabList onChange={handleChange} aria-label="Operational Units Tabs">
									<Tab label="Overview" value="overview" sx={{ textTransform: "uppercase" }} />
									<Tab label="Greenrouter" value="greenrouter" sx={{ textTransform: "uppercase" }} />
									<Tab
										label={t("third parties")}
										value="thirdparty"
										sx={{ textTransform: "uppercase" }}
									/>
								</TabList>
								{renderNavigationButton()}
							</Stack>
							<Stack direction="row" spacing={2} alignItems="flex-end">
								<DropdownSelect
									text={t("common:display for")}
									value={config.emissionsKey}
									loading={emissions.sites.isLoading}
									onChange={handleEmissionChange}
									dropdownItems={dropdownSelectEmissions}
									variant="standard"
								/>
							</Stack>
						</Stack>
					</Grid>

					<TabPanel value="overview" sx={{ padding: 0, width: "100%" }}>
						<Overview
							emissionsSites={emissions.sites}
							emissionsIntervals={emissions.intervalsGreenrouter}
							emissionsSitesSource={emissions.sitesSource}
							emissionsIntervalsPeriodType={config.overviewEmissionsIntervalsPeriodType}
							setEmissionsIntervalsPeriodType={(period) =>
								setConfig({ overviewEmissionsIntervalsPeriodType: period })
							}
							intervalMode={config.overviewIntervalMode}
							onIntervalModeChange={(intervalMode) => setConfig({ overviewIntervalMode: intervalMode })}
						/>
					</TabPanel>

					<TabPanel value="greenrouter" sx={{ padding: 0, width: "100%" }}>
						<Greenrouter
							emissionsSites={emissions.sites}
							from={from}
							to={to}
							emissionsSitesSource={emissions.sitesSource}
							emissionsConsumptionIntervals={consumption.intervals}
							consumptionSources={consumptionSources}
							loadingSources={consumption.isLoading}
							consumptionReload={consumption.reload}
							emissionsIntervalsPeriodType={config.overviewEmissionsIntervalsPeriodType}
							setEmissionsIntervalsPeriodType={(period) =>
								setConfig({ overviewEmissionsIntervalsPeriodType: period })
							}
							maps={maps}
							onBoundsChange={handleBoundsChange}
							mapBounds={mapBounds}
							greenrouterSelectedTabConsumption={config.greenrouterSelectedTabConsumption}
							setGreenrouterSelectedTabConsumption={(tab) =>
								setConfig({ greenrouterSelectedTabConsumption: tab })
							}
						/>
					</TabPanel>

					<TabPanel value="thirdparty" sx={{ padding: 0, width: "100%" }}>
						<ThirdParty
							emissionsSites={emissions.sites}
							calculationMethods={thirdParty.calucationMethods}
							nodesCertification={thirdParty.nodesCertification}
							environmentalKpi={thirdParty.environmanetlKpi}
							siteSource={thirdParty.siteSource}
						/>
					</TabPanel>
				</Grid>
			</TabContext>
		</View>
	);
}
